// Client de l'api Sensa Rappel (/api/v1). Le jeton du lien personnel part en
// `Authorization: Bearer` ; les réponses sont vérifiées par Zod. `zod/mini` (même
// bibliothèque, API fonctionnelle) : l'API chaînée de `zod` pèse ~85 kB dans le paquet
// principal, car ses méthodes ne s'élaguent pas.
import { z } from 'zod/mini';

import { jetonCourant } from './jeton';

export const BASE_API = `${import.meta.env.VITE_API_BASE_URL ?? ''}/api/v1`;

export class ErreurApi extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null,
  ) {
    super(code ? `HTTP ${status} (${code})` : `HTTP ${status}`);
    this.name = 'ErreurApi';
  }
}

async function appel(chemin: string, init: RequestInit = {}): Promise<unknown> {
  const jeton = jetonCourant();
  const reponse = await fetch(`${BASE_API}${chemin}`, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(jeton ? { Authorization: `Bearer ${jeton}` } : {}),
    },
    signal: AbortSignal.timeout(20_000),
    cache: 'no-store',
  });
  const corps: unknown = await reponse.json().catch(() => null);
  if (!reponse.ok) {
    const code = z.object({ code: z.string() }).safeParse(corps);
    throw new ErreurApi(reponse.status, code.success ? code.data.code : null);
  }
  return corps;
}

const date = z.coerce.date();

export const FAMILLES = [
  'estroprogestatif',
  'microval',
  'desogestrel',
  'notice-seulement',
  'inconnue',
] as const;
export type Famille = (typeof FAMILLES)[number];

export const CONFIRMATIONS = ['probleme', 'chaque-prise'] as const;
export type Confirmation = (typeof CONFIRMATIONS)[number];

const reglagesElle = z.object({
  heure: z.string(),
  fuseau: z.string(),
  schema: z.enum(['21/7', '24/4', 'continu']),
  inactifs: z.enum(['arret', 'placebo']),
  debutPlaquette: z.nullable(z.string()),
  famille: z.enum(FAMILLES),
  relanceMinutes: z.number(),
  alerteMinutes: z.number(),
  ackDelaiMinutes: z.number(),
  fenetreSansFamilleH: z.number(),
  prevenirProche: z.boolean(),
  texteNotification: z.string(),
});
export type Reglages = z.infer<typeof reglagesElle>;

const moi = z.object({
  role: z.enum(['elle', 'proche']),
  consentement: z.object({
    le: z.nullable(date),
    version: z.nullable(z.string()),
    versionCourante: z.string(),
  }),
  reglagesComplets: z.boolean(),
  reglages: z.union([
    reglagesElle,
    z.object({ heure: z.string(), fuseau: z.string(), prevenirProche: z.boolean() }),
  ]),
  procheInvite: z.boolean(),
  procheActif: z.boolean(),
  abonnements: z.number(),
  /** Le dernier appareil a été désabonné par le service de notification (#41). */
  abonnementPerduLe: z.nullable(date),
  /** Chez elle : le proche a perdu son dernier appareil abonné ; null chez le proche (#41). */
  procheAbonnementPerduLe: z.nullable(date),
  /** Ce que le proche reçoit (#27) ; null tant qu'il n'a pas choisi, et chez elle. */
  confirmation: z.nullable(z.enum(CONFIRMATIONS)),
  clePublique: z.string(),
});
export type Moi = z.infer<typeof moi>;

const source = z.object({
  editeur: z.string(),
  titre: z.string(),
  url: z.string(),
  pages: z.optional(z.string()),
  dates: z.string(),
  consulteLe: z.string(),
});

const regle = z.object({
  famille: z.enum(FAMILLES),
  libelle: z.string(),
  delaiH: z.nullable(z.number()),
  dansLeDelai: z.array(z.string()),
  apresLeDelai: z.array(z.string()),
  source,
  relu: z.boolean(),
  aVerifier: z.optional(z.string()),
});
export type Regle = z.infer<typeof regle>;

const regles = z.object({
  version: z.string(),
  renvoi: z.string(),
  regles: z.array(regle),
});

const jour = z.object({
  genre: z.enum(['avant-debut', 'actif', 'arret', 'placebo']),
  date: z.string(),
  debut: z.optional(z.string()),
  rang: z.optional(z.number()),
  reprise: z.optional(z.string()),
  repriseDans: z.optional(z.number()),
});

const aujourdhui = z.discriminatedUnion('reglagesComplets', [
  z.object({ reglagesComplets: z.literal(false) }),
  z.object({
    reglagesComplets: z.literal(true),
    date: z.string(),
    /** De quel comprimé parle l'écran (#26). */
    cible: z.enum(['ce-soir', 'hier']),
    jour,
    heure: date,
    finFenetre: date,
    prise: z.nullable(
      z.object({
        le: date,
        statut: z.enum(['avance', 'a-l-heure', 'apres-delai']),
        annulableJusqua: date,
      }),
    ),
    /** Le comprimé d'hier, rattrapé il y a moins de 10 min : encore annulable. */
    rattrape: z.nullable(z.object({ date: z.string(), le: date, annulableJusqua: date })),
    envois: z.object({
      nombre: z.number(),
      premierEnvoiLe: z.nullable(date),
      premierRecuLe: z.nullable(date),
      // « sans-accuse » : envoyé, mais ce téléphone n'a jamais accusé (#25).
      reception: z.nullable(z.enum(['recu', 'non-recu', 'en-attente', 'sans-accuse'])),
    }),
    regle: z.nullable(z.extend(regle, { version: z.string() })),
  }),
]);
export type Aujourdhui = z.infer<typeof aujourdhui>;
export type JourComplet = Extract<Aujourdhui, { reglagesComplets: true }>;

const historique = z.object({
  jours: z.array(
    z.object({
      date: z.string(),
      genre: z.enum(['avant-debut', 'actif', 'arret', 'placebo']),
      rang: z.nullable(z.number()),
      etat: z.enum(['arret', 'pris', 'pris-apres-delai', 'placebo', 'a-venir', 'non-confirme']),
      priseLe: z.nullable(date),
    }),
  ),
});
export type Historique = z.infer<typeof historique>;

const test = z.object({
  envois: z.array(
    z.object({ envoiId: z.string(), statut: z.nullable(z.number()), plateforme: z.string() }),
  ),
});

const json = (corps: unknown) => JSON.stringify(corps);

export const api = {
  moi: async () => moi.parse(await appel('/moi')),
  regles: async () => regles.parse(await appel('/regles')),
  consentir: (version: string) =>
    appel('/consentement', { method: 'POST', body: json({ version }) }),
  reglages: async (maj: Partial<Reglages> & { rangAujourdhui?: number }) =>
    reglagesElle.parse(await appel('/reglages', { method: 'PUT', body: json(maj) })),
  abonner: (abonnement: PushSubscriptionJSON, plateforme: string) =>
    appel('/abonnements', {
      method: 'POST',
      body: json({ endpoint: abonnement.endpoint, keys: abonnement.keys, plateforme }),
    }),
  desabonner: (endpoint: string) =>
    appel('/abonnements', { method: 'DELETE', body: json({ endpoint }) }),
  tester: async () => test.parse(await appel('/abonnements/test', { method: 'POST' })),
  /** L'accusé de la notification de test est-il arrivé au serveur ? (#25) */
  verifierTest: async (envoiId: string) =>
    z
      .object({ recu: z.boolean() })
      .parse(await appel(`/abonnements/test/${encodeURIComponent(envoiId)}`)),
  choisirConfirmation: (confirmation: Confirmation) =>
    appel('/confirmation', { method: 'PUT', body: json({ confirmation }) }),
  aujourdhui: async () => aujourdhui.parse(await appel('/aujourdhui')),
  prendre: async () => aujourdhui.parse(await appel('/prise', { method: 'POST' })),
  annuler: (jourPris: string) => appel(`/prise/${jourPris}`, { method: 'DELETE' }),
  historique: async () => historique.parse(await appel('/historique')),
  exporter: () => appel('/export'),
  effacer: () => appel('/moi', { method: 'DELETE' }),
};
